import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './PrivacyScreen.css';

const PrivacyScreen = () => {
  return (
    <div className="privacy-page">
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            <Card className="modern-card">
              <Card.Body className="p-5">
                <div className="text-center mb-5">
                  <h1 className="privacy-title">Privacy Policy</h1>
                  <p className="privacy-subtitle text-secondary">
                    Last updated: 8/1/2026
                  </p>
                </div>

                <div className="privacy-content">
                  <section className="privacy-section">
                    <h2>Introduction</h2>
                    <p>
                      League Runner ("we," "our," or "us") is committed to protecting your privacy. 
                      This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                      information when you use our running league management platform.
                    </p>
                  </section>

                  <section className="privacy-section">
                    <h2>Information We Collect</h2>
                    
                    <h3>Personal Information</h3>
                    <ul>
                      <li><strong>Account Information:</strong> Username, email address, first name, last name</li>
                      <li><strong>Profile Information:</strong> Running preferences, team affiliations, league memberships</li>
                      <li><strong>Activity Data:</strong> Running activities, distances, times, and performance metrics</li>
                    </ul>

                    <h3>Strava Integration</h3>
                    <ul>
                      <li><strong>Activity Data:</strong> Running activities imported from your Strava account (with your consent)</li>
                      <li><strong>Profile Information:</strong> Basic Strava profile information necessary for integration</li>
                      <li><strong>Performance Metrics:</strong> Distance, pace, elevation, and other activity details</li>
                    </ul>

                    <h3>Automatically Collected Information</h3>
                    <ul>
                      <li><strong>Usage Data:</strong> How you interact with our platform</li>
                      <li><strong>Device Information:</strong> Browser type, operating system, IP address</li>
                      <li><strong>Cookies:</strong> Authentication tokens, security cookies, consent choices, and preferences</li>
                    </ul>
                  </section>

                  <section className="privacy-section">
                    <h2>How We Use Your Information</h2>
                    <ul>
                      <li><strong>Service Provision:</strong> To provide and maintain our running league platform</li>
                      <li><strong>Account Management:</strong> To create and manage your user account</li>
                      <li><strong>League Operations:</strong> To facilitate draft processes, team management, and competition tracking</li>
                      <li><strong>Performance Tracking:</strong> To analyze and display your running performance and statistics</li>
                      <li><strong>Communication:</strong> To send you important updates about your leagues and teams</li>
                      <li><strong>Platform Improvement:</strong> To analyze usage patterns and improve our services</li>
                    </ul>
                  </section>

                  <section className="privacy-section">
                    <h2>Information Sharing and Disclosure</h2>
                    
                    <h3>Within League Runner</h3>
                    <ul>
                      <li><strong>League Members:</strong> Your running statistics and team information are visible to other league members</li>
                      <li><strong>Team Members:</strong> Your performance data is shared with your team for competitive purposes</li>
                      <li><strong>League Administrators:</strong> May access your information for league management purposes</li>
                    </ul>

                    <h3>Third-Party Services</h3>
                    <ul>
                      <li><strong>Strava:</strong> We connect to Strava's API to import your activity data (with your authorization)</li>
                      <li><strong>Stripe:</strong> We use Stripe to process subscriptions, one-time ad-free purchases, invoices, and billing portal actions</li>
                      <li><strong>Google:</strong> We use Google Analytics for all users and Google AdSense for users who are not ad-free</li>
                      <li><strong>Hosting Providers:</strong> Our platform is hosted on secure cloud infrastructure</li>
                      <li><strong>Analytics:</strong> We use analytics services to understand platform usage and improve product quality</li>
                    </ul>

                    <h3>Legal Requirements</h3>
                    <p>
                      We may disclose your information if required by law or if we believe disclosure is 
                      necessary to protect our rights, your safety, or the safety of others.
                    </p>
                  </section>

                  <section className="privacy-section">
                    <h2>Data Security</h2>
                    <ul>
                      <li><strong>Encryption:</strong> We use HTTPS encryption for all data transmission</li>
                      <li><strong>Authentication:</strong> Secure JWT token-based authentication system</li>
                      <li><strong>Access Controls:</strong> Limited access to personal data on a need-to-know basis</li>
                      <li><strong>Regular Updates:</strong> We keep our security measures up to date</li>
                    </ul>
                    <p>
                      While we implement strong security measures, no internet transmission is 100% secure. 
                      We cannot guarantee absolute security of your information.
                    </p>
                  </section>

                  <section className="privacy-section">
                    <h2>Your Rights and Choices</h2>
                    
                    <h3>Account Control</h3>
                    <ul>
                      <li><strong>Profile Updates:</strong> You can update your profile information at any time</li>
                      <li><strong>Strava Disconnection:</strong> You can disconnect your Strava account from our platform</li>
                      <li><strong>League Participation:</strong> You can choose which leagues to join or leave</li>
                    </ul>

                    <h3>Data Requests</h3>
                    <ul>
                      <li><strong>Access:</strong> Request a copy of your personal information</li>
                      <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                      <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
                    </ul>
                  </section>

                  <section className="privacy-section">
                    <h2>Cookies and Tracking</h2>
                    <p>
                      We use cookies and similar technologies to:
                    </p>
                    <ul>
                      <li>Maintain your login session</li>
                      <li>Remember your preferences and account settings</li>
                      <li>Analyze platform usage across the service</li>
                      <li>Serve advertising placements to users who are not ad-free</li>
                      <li>Improve user experience and billing support workflows</li>
                    </ul>
                    <p>
                      Essential cookies are required to authenticate your account and secure the service.
                      Analytics remain enabled to help us operate and improve League Runner. Advertising is
                      disabled only for paid ad-free accounts and users the site admin has marked as ad-free.
                    </p>
                  </section>

                  <section className="privacy-section">
                    <h2>Billing and Purchases</h2>
                    <p>
                      Paid subscriptions and one-time ad-free purchases are processed by Stripe. League Runner
                      does not store full payment card numbers. We retain billing status, Stripe customer IDs,
                      subscription identifiers, and related transaction metadata needed to manage access,
                      resolve disputes, and provide support.
                    </p>
                  </section>

                  <section className="privacy-section">
                    <h2>Children's Privacy</h2>
                    <p>
                      League Runner is not intended for users under 13 years of age. We do not knowingly 
                      collect personal information from children under 13. If you are a parent or guardian 
                      and believe your child has provided us with personal information, please contact us.
                    </p>
                  </section>

                  <section className="privacy-section">
                    <h2>Changes to This Privacy Policy</h2>
                    <p>
                      We may update this Privacy Policy from time to time. We will notify you of any 
                      material changes by:
                    </p>
                    <ul>
                      <li>Updating the "Last updated" date at the top of this policy</li>
                      <li>Sending an email notification for significant changes</li>
                      <li>Displaying a prominent notice on our platform</li>
                    </ul>
                  </section>

                  <section className="privacy-section">
                    <h2>Contact Information</h2>
                    <p>
                      If you have any questions about this Privacy Policy or our privacy practices, 
                      please contact us at:
                    </p>
                    <div className="contact-info">
                      <p><strong>Email:</strong> ethan@leaguerunner.net</p>
                    </div>
                  </section>

                  <div className="privacy-footer">
                    <hr className="my-5" />
                    <div className="text-center">
                      <Link to="/" className="btn btn-primary me-3">
                        Back to Home
                      </Link>
                      <Link to="/terms" className="btn btn-outline-secondary">
                        Terms of Service
                      </Link>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PrivacyScreen;
